import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article
    ? categories.find((c) => c.slug === article.category)
    : null;

  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-600 text-sm mb-6">你访问的文章可能已被移除或不存在。</p>
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '正太漫画研究所',
    },
    url: typeof window !== 'undefined' ? window.location.href : '',
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin + '/' : '',
      },
      category
        ? {
            '@type': 'ListItem',
            position: 2,
            name: category.name,
            item: typeof window !== 'undefined'
              ? window.location.origin + '/category/' + category.slug
              : '',
          }
        : null,
      {
        '@type': 'ListItem',
        position: category ? 3 : 2,
        name: article.title,
      },
    ].filter(Boolean),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                ...(category ? [{ label: category.name, to: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </header>

            {/* Cover Image */}
            <div className="relative w-full h-[300px] md:h-[420px] rounded-xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            {/* Article Content */}
            <div
              className="prose-custom max-w-none mb-12"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Article Footer */}
            <div className="border-t border-background-200/70 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
              <Link
                to="/"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16 pt-12 border-t border-background-200/70">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.slug} {...a} />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}